// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import * as R from "ramda";

import {
  AssistantIcon,
  ReminderPrompt,
} from "src/components/messages/Prompt/styles";
import { FlexSpacer } from "src/components/messages/Chat/styles";
import WarningModal from "src/components/Modal/Warning";
import MessageCard from "src/components/messages/Chat/MessageCard";
import Icon from "src/icons";
import ChatTimestamp from "src/components/messages/Chat/ChatTimestamp";
import MoreMenu from "src/components/messages/MoreMenu";
import SelectionIndicator from "src/components/messages/Chat/SelectIndicator";
import {
  selectMessage,
  deselectMessage,
  hideSelectOptions,
  showMobileOptions,
} from "src/actions/chatmenu";
import { setAttribute } from "src/actions/chatroom";
import {
  getChatroomReminders,
  getChatroomMembership,
  getSelectedAction,
} from "src/reducers";
import MessageWithMentions from "src/components/messages/MessageWithMentions";
import useBoolean from "src/hooks/useBoolean";
import { MessageHeader } from "src/components/messages/Chat/styles";

import type { AppState, Message, UID, MessageID } from "src/types";

import {
  MessageText,
  ToggleText,
  AssistantTitle,
  MutedToggleText,
} from "./styles";

type Props = {
  message: Message,
  _setAttribute: Function,
  reminders: boolean,
  selectedAction: string,
  isSrwMobile: boolean,
  isMember: boolean,
  currentUser: UID,
  _selectMessage: Function,
  _deselectMessage: Function,
  _hideSelectOptions: Function,
  _showMobileOptions: Function,
  selectedMessages: Array<MessageID>,
};

const ReminderUniBot = ({
  message,
  _setAttribute,
  reminders,
  selectedAction,
  isSrwMobile,
  currentUser,
  _selectMessage,
  _deselectMessage,
  _hideSelectOptions,
  _showMobileOptions,
  selectedMessages,
}: Props) => {
  const [menu, setMenu] = useState(false);
  const { data, roomId, timestamp, delivered, success, author, id } = message;

  const {
    value: modal,
    setTrue: showModal,
    setFalse: hideModal,
  } = useBoolean();
  const outerRef = useRef();

  const turnOffReminders = () => {
    _setAttribute(roomId, { reminders: false });
    hideModal();
  };

  // Render action menu when user is deleting or forwarding message(s)
  const showMenuActions = useCallback(() => {
    const currentActionFilter =
      selectedAction.length == 0 ||
      (selectedAction !== "DELETE" && selectedAction !== "FORWARD");

    if (currentActionFilter) setMenu(true);
    else setMenu(false);
  }, [menu, selectedAction]);

  const isOwner = currentUser === author;
  const isMessageSelected = selectedMessages.includes(id);

  const toggleMenu = useCallback(() => {
    if (selectedAction) {
      if (selectedAction === "MOBILE") {
        if (!isMessageSelected) {
          _hideSelectOptions();
          _showMobileOptions();
          _selectMessage(id, timestamp, isOwner);
        } else _deselectMessage(id);
      } else if (!isMessageSelected) _selectMessage(id, timestamp, isOwner);
      else _deselectMessage(id);
    }
  }, [selectedAction, id, timestamp, isOwner, isMessageSelected]);

  const handleMouseLeave = () => {
    setMenu(false);
  };

  return (
    <ReminderPrompt
      onMouseEnter={showMenuActions}
      onMouseLeave={handleMouseLeave}
      isSrwMobile={isSrwMobile}
      onClick={toggleMenu}
      showSelect={selectedAction && selectedAction !== "MOBILE"}
    >
      <SelectionIndicator id={id} timestamp={timestamp} isOwner={isOwner} />
      {modal && (
        <WarningModal
          handleClose={hideModal}
          handleClick={turnOffReminders}
          heading="Turn off reminders"
          content="Are you sure you want to turn off reminders?"
          primaryText="Turn off"
          secondaryText="Cancel"
          outerRef={outerRef}
        />
      )}
      <AssistantIcon>
        <Icon type="assistant" />
      </AssistantIcon>
      <MessageCard first>
        <MessageHeader>
          <AssistantTitle>{i18n.t(k.UNIFIZE_ASSISTANT)}</AssistantTitle>
          <ChatTimestamp
            read={success ?? false}
            delivered={delivered}
            timestamp={timestamp}
          />
        </MessageHeader>
        <MessageText>
          <MessageWithMentions message={data || ""} />
        </MessageText>
        {R.isNil(reminders) || reminders ? (
          <ToggleText onClick={showModal}>
            {i18n.t(k.TURN_OFF_REMINDERS)}
          </ToggleText>
        ) : (
          <MutedToggleText>
            {i18n.t(k.REMINDERS_HAVE_BEEN_TURNED_OFF)}
          </MutedToggleText>
        )}
      </MessageCard>
      <FlexSpacer />
      {menu && !isSrwMobile ? (
        <MoreMenu
          id={id}
          timestamp={timestamp}
          isOwner={isOwner}
          author={author}
        />
      ) : null}
    </ReminderPrompt>
  );
};

const mapStateToProps = ({ app }: { app: AppState }, { message }) => ({
  reminders: getChatroomReminders(app, message.roomId),
  selectedAction: getSelectedAction(app),
  isSrwMobile: app.srw.isMobile,
  isMember: getChatroomMembership(app, app.chatRooms.current),
  currentUser: app.currentUser.uid,
  selectedMessages: app.chatRooms.selectedMessages,
});

export default connect(mapStateToProps, {
  _setAttribute: setAttribute,
  _selectMessage: selectMessage,
  _deselectMessage: deselectMessage,
  _hideSelectOptions: hideSelectOptions,
  _showMobileOptions: showMobileOptions,
})(ReminderUniBot);
